import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { RootState } from '../../app/store';
import sitesService from '../../common/api/sitesService';
import { LOADING_STATUS } from '../../common/constants';

export interface Site {
  id: string;
  name: string;
}

export interface SitesState {
  sites: Site[];
  sitesForAccount: Site[];
  sitesLoadingStatus: LOADING_STATUS;
  sitesForAccountLoadingStatus: LOADING_STATUS;
}

const initialState: SitesState = {
  sites: [],
  sitesForAccount: [],
  sitesLoadingStatus: LOADING_STATUS.IDLE,
  sitesForAccountLoadingStatus: LOADING_STATUS.IDLE,
};

export const fetchSitesAsync = createAsyncThunk(
  'sites/fetchSites',
  async (_, { rejectWithValue }) => {
    try {
      return await sitesService.getSites();
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const fetchAccountSitesAsync = createAsyncThunk(
  'sites/fetchAccountSites',
  async (accountId: string, { rejectWithValue }) => {
    try {
      return await sitesService.getAccountSites(accountId);
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const modifyAccountSitesAsync = createAsyncThunk(
  'sites/modifyAccountSites',
  async (
    { accountId, selectedSites }: { accountId: string; selectedSites: Site[] },
    { rejectWithValue },
  ) => {
    try {
      return await sitesService.updateAccountSites(accountId, selectedSites);
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

const sitesSlice = createSlice({
  name: 'sites',
  initialState,
  reducers: {
    updateSitesForAccount: (state, action) => {
      const { site, checked } = action.payload;
      state.sitesForAccount = checked
        ? [...state.sitesForAccount, site]
        : state.sitesForAccount.filter(s => s.id !== site.id);
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchSitesAsync.pending, state => {
        state.sitesLoadingStatus = LOADING_STATUS.LOADING;
      })
      .addCase(fetchSitesAsync.fulfilled, (state, action) => {
        state.sitesLoadingStatus = LOADING_STATUS.IDLE;
        state.sites = action.payload;
      })
      .addCase(fetchSitesAsync.rejected, state => {
        state.sitesLoadingStatus = LOADING_STATUS.FAILED;
      })
      .addCase(fetchAccountSitesAsync.pending, state => {
        state.sitesForAccountLoadingStatus = LOADING_STATUS.LOADING;
      })
      .addCase(fetchAccountSitesAsync.fulfilled, (state, action) => {
        state.sitesForAccountLoadingStatus = LOADING_STATUS.IDLE;
        state.sitesForAccount = action.payload;
      })
      .addCase(fetchAccountSitesAsync.rejected, state => {
        state.sitesForAccountLoadingStatus = LOADING_STATUS.FAILED;
      })
      .addCase(modifyAccountSitesAsync.pending, state => {
        state.sitesForAccountLoadingStatus = LOADING_STATUS.LOADING;
      })
      .addCase(modifyAccountSitesAsync.fulfilled, (state, action) => {
        state.sitesForAccountLoadingStatus = LOADING_STATUS.IDLE;
      })
      .addCase(modifyAccountSitesAsync.rejected, state => {
        state.sitesForAccountLoadingStatus = LOADING_STATUS.FAILED;
      });
  },
});

export const selectSites = (state: RootState) => state.sites.sites;
export const selectSitesForAccount = (state: RootState) =>
  state.sites.sitesForAccount;
export const selectSitesLoadingStatus = (state: RootState) =>
  state.sites.sitesLoadingStatus;
export const selectSitesForAccountLoadingStatus = (state: RootState) =>
  state.sites.sitesForAccountLoadingStatus;

export const { updateSitesForAccount } = sitesSlice.actions;

export default sitesSlice.reducer;
